import { inject, Injectable } from '@angular/core';
import { AmendmentValidationCircuit } from '@app/features/amendments/amendment-validation-circuit';
import { ContractValidationService } from '@app/features/contracts/contract-validation-service';
import { ValidationRequestService } from '@app/features/contracts/validation/validation-request-service';
import {
  ValidationRequest,
  ValidationRequestType,
} from '@app/features/contracts/validation/validation-request.model';
import { ValidationSubjectItem } from '@app/features/contracts/validation/validation-subject.model';
import { Observable } from 'rxjs';

/** Les gestes du circuit sur un sujet : les mêmes pour un contrat et pour un avenant. */
export interface ValidationCircuit {
  /** Refuse de demander ou d'accepter une validation sur un sujet incomplet. */
  verify(subjectId: string): Observable<void>;
  request(
    subjectId: string,
    requestedTo: string,
    type: ValidationRequestType,
    comment: string | null,
  ): Observable<ValidationRequest>;
  /** @param refresh relire le sujet après l'acceptation, sauf si une demande la suit */
  accept(subjectId: string, refresh?: boolean): Observable<ValidationRequest>;
  refuse(subjectId: string, comment: string): Observable<ValidationRequest>;
  cancel(subjectId: string, comment: string): Observable<ValidationRequest>;
}

/** Le circuit du contrat d'embauche, tel qu'il existait. */
@Injectable({ providedIn: 'root' })
export class HiringValidationCircuit implements ValidationCircuit {
  private validationRequestService = inject(ValidationRequestService);
  private contractValidationService = inject(ContractValidationService);

  verify(contractId: string): Observable<void> {
    return this.contractValidationService.verify(contractId);
  }

  request(
    contractId: string,
    requestedTo: string,
    type: ValidationRequestType,
    comment: string | null,
  ): Observable<ValidationRequest> {
    return this.validationRequestService.requestValidation({
      contractId,
      requestedTo,
      type,
      comment,
    });
  }

  accept(contractId: string, refresh = true): Observable<ValidationRequest> {
    return this.validationRequestService.acceptValidation(contractId, refresh);
  }

  refuse(contractId: string, comment: string): Observable<ValidationRequest> {
    return this.validationRequestService.refuseValidation(contractId, comment);
  }

  cancel(contractId: string, comment: string): Observable<ValidationRequest> {
    return this.validationRequestService.cancelValidation(contractId, comment);
  }
}

/** Le circuit de chaque sorte de sujet. */
@Injectable({ providedIn: 'root' })
export class ValidationCircuits {
  private hiring = inject(HiringValidationCircuit);
  private amendment = inject(AmendmentValidationCircuit);

  of(subject: ValidationSubjectItem): ValidationCircuit {
    return subject.subjectKind === 'amendment' ? this.amendment : this.hiring;
  }
}
